import { ArrowRight } from "react-bootstrap-icons";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import Footer from "../../components/Footer/Footer";
import catlogicLogo from "../../assets/logo_white.svg";
import SiteNav from "../../components/SiteNav/SiteNav";
import Seo, { SITE_URL } from "../../components/Seo/Seo";
import { AI_COPY } from "../../content/localizedPageCopy";
import { useLocale } from "../../contexts/LocaleContext";
import { navigateToHomeSection } from "../../utils/homeSectionNavigation";
import { createHeroParticles } from "../../utils/heroParticles";
import styles from "./Ai.module.scss";

export default function AiView() {
  const nav = useNavigate();
  const { locale } = useLocale();
  const copy = AI_COPY[locale];
  const heroParticles = useMemo(() => createHeroParticles(), []);

  const structuredData = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: copy.heroTitle,
        description: copy.metaDescription,
        url: `${SITE_URL}/ai`,
        serviceType: "AI business consulting and implementation",
        areaServed: "BY",
        provider: {
          "@type": "Organization",
          name: "CatLogic",
          url: SITE_URL,
        },
        additionalType: "https://karpuk.by/",
      },
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: copy.expertName,
        jobTitle: copy.expertRole,
        description: copy.expertBody,
        url: "https://karpuk.by/",
        worksFor: {
          "@type": "Organization",
          name: "CatLogic",
          url: SITE_URL,
        },
      },
    ],
    [copy]
  );

  return (
    <main className={styles.page}>
      <Seo
        title={copy.pageTitle}
        description={copy.metaDescription}
        keywords={copy.metaKeywords}
        path="/ai"
        structuredData={structuredData}
      />

      <SiteNav solidThreshold={320} />

      <section className={styles.hero}>
        <div className={styles.heroParticles} aria-hidden="true">
          {heroParticles.map((particle, index) => (
            <span
              key={index}
              className={styles.particle}
              style={
                {
                  width: particle.size,
                  height: particle.size,
                  top: particle.top,
                  left: particle.left,
                  background: `radial-gradient(circle, ${particle.color}, transparent 70%)`,
                  animationDuration: particle.duration,
                  animationDelay: particle.delay,
                  "--particle-scale-mid": particle.scaleMid,
                  "--particle-scale-low": particle.scaleLow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={styles.container}>
          <div className={styles.heroCenter}>
            <img className={styles.heroLogo} src={catlogicLogo} alt="CatLogic" />
            <span className={styles.heroCaption}>{copy.badge}</span>

            <h1 className={styles.heroTitle}>{copy.heroTitle}</h1>
            <p className={styles.heroDescription}>{copy.heroBody}</p>

            <div className={styles.heroActions}>
              <button type="button" className={styles.primaryButton} onClick={() => navigateToHomeSection(nav, "contacts")}>
                {copy.primaryCta} <ArrowRight />
              </button>
              <a className={styles.secondaryButton} href="#pain">
                {copy.secondaryCta} <ArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.surface}>
        <section className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.expertPanel}>
              <div className={styles.expertMeta}>
                <span className={styles.kicker}>{copy.expertKicker}</span>
                <h2>{copy.expertName}</h2>
                <span className={styles.expertRole}>{copy.expertRole}</span>
              </div>

              <div className={styles.expertContent}>
                <p>{copy.expertBody}</p>
                <div className={styles.expertPills}>
                  {copy.expertHighlights.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="pain" className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <span className={styles.kicker}>{copy.painKicker}</span>
              <h2>{copy.painTitle}</h2>
            </div>

            <div className={styles.painGrid}>
              {copy.painPoints.map((item) => (
                <article key={item.title} className={styles.painCard}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <p className={styles.painResponse}>{item.response}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.darkBand}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <span className={styles.kicker}>{copy.processKicker}</span>
              <h2>{copy.processTitle}</h2>
              <p>{copy.processBody}</p>
            </div>

            <div className={styles.processGrid}>
              {copy.processSteps.map((step, index) => (
                <div key={step.title} className={styles.processStep}>
                  <span className={styles.processIndex}>{`0${index + 1}`}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <span className={styles.kicker}>{copy.tiersKicker}</span>
              <h2>{copy.tiersTitle}</h2>
              <p>{copy.tiersBody}</p>
            </div>

            <div className={styles.tiersGrid}>
              {copy.tiers.map((tier) => (
                <article key={tier.title} className={styles.tierCard}>
                  <span className={styles.tierLabel}>{tier.label}</span>
                  <h3>{tier.title}</h3>
                  <p>{tier.body}</p>
                  <p className={styles.tierOutcome}>{tier.outcome}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.darkBand}>
          <div className={styles.container}>
            <div className={styles.ctaPanel}>
              <span className={styles.kicker}>{copy.ctaKicker}</span>
              <h2>{copy.ctaTitle}</h2>
              <p>{copy.ctaBody}</p>

              <div className={styles.ctaActions}>
                <button type="button" className={styles.ctaPrimaryButton} onClick={() => navigateToHomeSection(nav, "contacts")}>
                  {copy.ctaPrimary} <ArrowRight />
                </button>
                <button type="button" className={styles.ctaSecondaryButton} onClick={() => navigateToHomeSection(nav, "services")}>
                  {copy.ctaSecondary} <ArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </main>
  );
}
